import React from 'react'
import styled from 'styled-components'
import { Block, P } from '@mote/ui'
import { ArrowRightIcon } from '@mote/assets/icons'

/* 
=====================
Data Object
=====================

{
  description: string,
  diverging: {
    value: {
      amount: number                 // old value
      prefix: string | React.Element // optional
      suffix: string | React.Element // optional
    }
  },
  value: {
    amount: number                 // current value
    prefix: string | React.Element // optional
    suffix: string | React.Element // optional
  }
}

*/

const DIVERGING_STATE = {
  NEUTRAL: 'NEUTRAL',
  POSITIVE: 'POSITIVE',
  NEGATIVE: 'NEGATIVE'
}

function calculatePercentageIncrease(startingValue, finalValue) {
  const value = ((finalValue - startingValue) / startingValue) * 100
  const sign = value > 0 ? '+' : ''

  return sign + value.toFixed(2) + '%'
}

function getDivergingState(data) {
  if (!data.diverging) return null

  if (data.diverging.value.amount === data.value.amount)
    return DIVERGING_STATE.NEUTRAL
  if (data.diverging.value.amount > data.value.amount)
    return DIVERGING_STATE.NEGATIVE

  return DIVERGING_STATE.POSITIVE
}

export const Stat = ({ data }) => {
  const divergingState = React.useMemo(() => getDivergingState(data), [])

  return (
    <Root layout='vertical' justify='center' gap={8} spreadX>
      <DetailsContainer layout='horizontal' align='center' gap={8}>
        <Value fontSize={24} weight={600} lineHeight='100%'>
          {data.value.prefix}
          {data.value.amount}
          {data.value.suffix}
        </Value>
        {data.diverging && (
          <DivergingValueContainer
            layout='horizontal'
            align='center'
            gap={6}
            state={divergingState}
          >
            <ArrowIcon color='currentColor' state={divergingState} />
            <DivergingValue
              fontSize={18}
              fontWeight={400}
              lineHeight='100%'
              color='inherit'
            >
              {data.diverging.value.prefix}
              {calculatePercentageIncrease(
                data.diverging.value.amount,
                data.value.amount
              )}
              {data.diverging.value.suffix}
            </DivergingValue>
          </DivergingValueContainer>
        )}
      </DetailsContainer>
      {data.description && (
        <Description
          fontSize={15}
          fontWeight={400}
          lineHeight='120%'
          color='#6C6C84'
        >
          {data.description}
        </Description>
      )}
    </Root>
  )
}

const Root = styled(Block)`
  padding: 24px;
  background: #ffffff;
  border-left: 1px solid #d1d1e0;
  transition: all 150ms ease-in;

  &:hover {
    background: #f6f6f9;
    border-color: #ac0ae8;
  }
`

const DivergingValueContainer = styled(Block)`
  color: ${({ state }) => {
    switch (state) {
      case DIVERGING_STATE.POSITIVE:
        return '#57C96A'
      case DIVERGING_STATE.NEGATIVE:
        return '#F44336'
      default:
        return '#25252D'
    }
  }};
`

const DivergingValue = styled.span`
  color: inherit;
  font-size: inherit;
  line-height: inherit;
`

const ArrowIcon = styled(ArrowRightIcon)`
  width: 24;
  height: 24;
  transform: rotate(
    ${({ state }) => (state === DIVERGING_STATE.POSITIVE ? -90 : 90)}deg
  );
`

const DetailsContainer = styled(Block)``
const Value = styled(P)``
const Description = styled(P)``
